import type { GeoLocation } from '@shared/api'

const EARTH_RADIUS_KM = 6371

const toRad = (deg: number) => (deg * Math.PI) / 180

/**
 * Great-circle distance. Returns undefined when either endpoint lacks
 * coordinates -- the geoip lookup makes `location` optional, and a missing
 * position must not become a distance of zero.
 */
export function distanceKm(a?: GeoLocation, b?: GeoLocation): number | undefined {
    if (a?.latitude === undefined || a?.longitude === undefined) return undefined
    if (b?.latitude === undefined || b?.longitude === undefined) return undefined

    const dLat = toRad(b.latitude - a.latitude)
    const dLon = toRad(b.longitude - a.longitude)
    const lat1 = toRad(a.latitude)
    const lat2 = toRad(b.latitude)

    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)

    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

export function median(values: number[]): number | undefined {
    if (values.length === 0) return undefined
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Equirectangular projection. Two lines, which is why d3-geo is not a dependency. */
export function project(lat: number, lon: number, width: number, height: number) {
    return {
        x: ((lon + 180) / 360) * width,
        y: ((90 - lat) / 180) * height,
    }
}
